// Recent semantic searches are clinical queries kept per case in localStorage
// (DocumentSorter's useRecentSearches). They must not outlive a logout on a
// shared workstation (MDRS-558), so the key lives here where both logout
// routines can reach it without importing from pages/.

const KEY_PREFIX = "recent-searches:";

// Set the moment logout starts, before any await, so a search response that
// lands while logout is still clearing other data can't write history back.
let writesBlocked = false;

// The same block for other tabs, which share localStorage but not the flag
// above: a search in another tab can land before logout clears the shared
// MSAL cache and sends that tab to the landing page. Holds "<timestamp>:<id>"
// and expires on its own, so a logout can't stop recording for good. Must not
// start with KEY_PREFIX, or clearRecentSearches would remove it.
const BLOCKED_AT_KEY = "medirect.recent-searches-blocked-at";
// Logout's own waits (recovery clear, MSAL cache clear) take a few seconds at most.
const SHARED_BLOCK_MS = 60_000;

// The value this tab last wrote to BLOCKED_AT_KEY. A reset only removes the
// shared block while it still holds this value: an OTP sign-in here must not
// lift a block another tab's logout still relies on.
let ownSharedBlock: string | null = null;

const isSharedBlockActive = (): boolean => {
  try {
    const blockedAt = Number(localStorage.getItem(BLOCKED_AT_KEY)?.split(":")[0]);
    if (!blockedAt) return false;
    const age = Date.now() - blockedAt;
    return age >= 0 && age < SHARED_BLOCK_MS;
  } catch {
    return false;
  }
};

/** Stops recording recent searches in this tab for the rest of this page
 * load, and in other tabs for a short while. */
export const blockRecentSearchWrites = (): void => {
  writesBlocked = true;
  ownSharedBlock = `${Date.now()}:${crypto.randomUUID()}`;
  try {
    localStorage.setItem(BLOCKED_AT_KEY, ownSharedBlock);
  } catch {
    /* localStorage unavailable — this tab's flag still holds. */
  }
};

export const areRecentSearchWritesBlocked = (): boolean =>
  writesBlocked || isSharedBlockActive();

/** Records searches again. Call once a new sign-in succeeds without a page
 * reload (OTP), which would otherwise leave the block set. Another tab's
 * block stays until it expires. */
export const resetRecentSearchWrites = (): void => {
  writesBlocked = false;
  try {
    if (
      ownSharedBlock !== null &&
      localStorage.getItem(BLOCKED_AT_KEY) === ownSharedBlock
    ) {
      localStorage.removeItem(BLOCKED_AT_KEY);
    }
  } catch {
    /* localStorage unavailable */
  }
  ownSharedBlock = null;
};

export const recentSearchesKey = (caseNumber: string) =>
  `${KEY_PREFIX}${caseNumber}`;

/** Removes every case's recent searches. Never throws — logout must not be blocked. */
export const clearRecentSearches = (): void => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(KEY_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    /* localStorage unavailable — best-effort. */
  }
};
